import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type PinnedItemType = "order" | "product";

export type OrderSnapshot = {
  id: number;
  amazonOrderId: string;
  status: string;
  imageUrl?: string;
  itemName?: string;
  barcode?: string;
};

export type ProductSnapshot = {
  asin: string;
  itemName?: string;
  imageUrl?: string;
  status?: string;
};

export type NoteItem = {
  uid: string;
  type: "note";
  text: string;
  createdAt: string;
};

export type PinnedItem = {
  uid: string;
  type: "order" | "product";
  pinnedAt: string;
  note: string;
  snapshot: OrderSnapshot | ProductSnapshot;
};

export type WorkspaceItem = NoteItem | PinnedItem;

type State = {
  isOpen: boolean;
  items: WorkspaceItem[];
};

type Action = {
  open: () => void;
  close: () => void;
  toggle: () => void;
  addNote: (_text: string) => void;
  removeItem: (_uid: string) => void;
  clearAll: () => void;
  setItemNote: (_uid: string, _note: string) => void;
  pinOrder: (_snapshot: OrderSnapshot) => void;
  pinProduct: (_snapshot: ProductSnapshot) => void;
  reorder: (_items: WorkspaceItem[]) => void;
  isPinned: (_type: PinnedItemType, _id: string | number) => boolean;
};

export const useWorkspaceStore = create<State & Action>()(
  persist(
    (set, get) => ({
      isOpen: false,
      items: [],

      open: () => set({ isOpen: true }),
      close: () => set({ isOpen: false }),
      toggle: () => set((s) => ({ isOpen: !s.isOpen })),

      addNote: (text) => {
        const uid = `note-${Date.now()}`;
        set((s) => ({
          items: [
            { uid, type: "note", text, createdAt: new Date().toISOString() },
            ...s.items,
          ],
        }));
      },

      removeItem: (uid) =>
        set((s) => ({ items: s.items.filter((i) => i.uid !== uid) })),

      clearAll: () => set({ items: [] }),

      setItemNote: (uid, note) =>
        set((s) => ({
          items: s.items.map((i) =>
            i.uid === uid && i.type !== "note" ? { ...i, note } : i,
          ),
        })),

      pinOrder: (snapshot) => {
        const uid = `order-${snapshot.id}-${Date.now()}`;
        set((s) => ({
          items: [
            {
              uid,
              type: "order",
              pinnedAt: new Date().toISOString(),
              note: "",
              snapshot,
            },
            ...s.items,
          ],
        }));
      },

      pinProduct: (snapshot) => {
        const uid = `product-${snapshot.asin}-${Date.now()}`;
        set((s) => ({
          items: [
            {
              uid,
              type: "product",
              pinnedAt: new Date().toISOString(),
              note: "",
              snapshot,
            },
            ...s.items,
          ],
        }));
      },

      reorder: (items) => set({ items }),

      isPinned: (type, id) => {
        return get().items.some((i) => {
          if (i.type === "note") return false;
          if (type === "order" && i.type === "order")
            return (i.snapshot as OrderSnapshot).id === id;
          if (type === "product" && i.type === "product")
            return (i.snapshot as ProductSnapshot).asin === id;
          return false;
        });
      },
    }),
    {
      name: "syncrosale-workspace",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ items: s.items }),
    },
  ),
);
